import React, { useCallback, useMemo, useRef, useState } from 'react';
import ArrowLeftLineIcon from 'remixicon-react/ArrowLeftLineIcon.js';
import ArrowRightLineIcon from 'remixicon-react/ArrowRightLineIcon.js';
import DownloadCloud2LineIcon from 'remixicon-react/DownloadCloud2LineIcon.js';
import { Button } from '../Button/index.js';
import { usePreloadImage } from '../hooks/usePreloadImage.js';
import { Modal } from '../Modal/index.js';
import { useKeypressListener } from '../utils/useKeypressListener.js';
import { MediaZoomContext } from './MediaZoomContext.js';
import { MediaZoomItem } from './MediaZoomItem.js';
import { downloadFile } from './utils.js';
export const MediaZoom = ({ children, zoom, download }) => {
    const [zoomed, setZoomed] = useState(false);
    const items = useRef([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const toggle = useCallback(() => {
        setZoomed(!zoomed);
    }, [zoomed]);
    const register = useCallback((item) => {
        if (!items.current.map(it => it.id).includes(item.id)) {
            items.current.push(item);
        }
    }, []);
    const unregister = useCallback((id) => {
        items.current = items.current.filter(it => it.id !== id);
    }, []);
    const openItem = useCallback((id) => {
        setActiveIndex(items.current.findIndex(it => it.id === id));
        setZoomed(true);
    }, []);
    const contextValue = useMemo(() => ({
        zoomEnabled: zoom,
        zoomed,
        activeIndex,
        register,
        unregister,
        toggle,
        openItem,
    }), [zoom, zoomed, activeIndex, register, unregister, toggle, openItem]);
    const activeItem = items.current[activeIndex];
    const { length } = items.current;
    const isPreviousButtonEnabled = activeIndex > 0;
    const isNextButtonEnabled = activeIndex < length - 1;
    const goToPrevious = () => {
        if (isPreviousButtonEnabled) {
            setActiveIndex(activeIndex - 1);
        }
    };
    const goToNext = () => {
        if (isNextButtonEnabled) {
            setActiveIndex(activeIndex + 1);
        }
    };
    useKeypressListener('ArrowLeft', goToPrevious);
    useKeypressListener('ArrowRight', goToNext);
    const [downloading, setDownloading] = useState(false);
    const handleDownload = async () => {
        const slide = activeItem;
        if (!(slide === null || slide === void 0 ? void 0 : slide.original) && !(slide === null || slide === void 0 ? void 0 : slide.downloadUrl)) {
            return;
        }
        setDownloading(true);
        if (slide.downloadUrl) {
            await downloadFile(slide.downloadUrl, slide.caption);
        }
        else {
            await downloadFile(slide.original);
        }
        setDownloading(false);
    };
    usePreloadImage({ src: activeItem === null || activeItem === void 0 ? void 0 : activeItem.original });
    return (React.createElement(MediaZoomContext.Provider, { value: contextValue },
        children,
        zoom && activeItem && (React.createElement(Modal, { open: zoomed, onClose: toggle, size: "full" },
            React.createElement(Modal.Header, { className: "space-s-3" },
                React.createElement("div", { className: "overflow-hidden grow" }, activeItem.caption && (React.createElement("p", { className: "truncate" }, activeItem.caption))),
                download && (React.createElement(Button, { size: "md", variant: "basic", className: "me-3", disabled: downloading, loading: downloading, leadingIcon: React.createElement(DownloadCloud2LineIcon, null), onClick: handleDownload, tabIndex: -1 }))),
            React.createElement(Modal.Content, { className: "flex flex-col justify-center" },
                React.createElement("div", { className: "grow min-h-0 flex items-center justify-center space-s-3" },
                    length > 1 && (React.createElement(Button, { variant: "outline", rounded: true, onClick: goToPrevious, disabled: !isPreviousButtonEnabled, leadingIcon: React.createElement(ArrowLeftLineIcon, { className: "rtl:-scale-x-100" }), className: "shrink-0" })),
                    React.createElement("div", { className: "grow min-w-0 h-full" },
                        React.createElement("img", { className: "max-h-[calc(100vh-10rem)] mx-auto object-scale-down", width: activeItem.originalWidth, height: activeItem.originalHeight, src: activeItem.original, alt: activeItem.alt })),
                    length > 1 && (React.createElement(Button, { variant: "outline", rounded: true, onClick: goToNext, disabled: !isNextButtonEnabled, leadingIcon: React.createElement(ArrowRightLineIcon, { className: "rtl:-scale-x-100" }), className: "shrink-0" }))))))));
};
MediaZoom.Item = MediaZoomItem;
//# sourceMappingURL=MediaZoom.js.map